// frontend/src/components/ThemeSwitcher.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardActionArea,
  Button,
  Divider,
  alpha,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  Tooltip,
  IconButton
} from
  '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  FormatColorFill as ColorIcon,
  TextFields as TypographyIcon,
  Check as CheckIcon,
  AccessibilityNew as AccessibilityIcon,
  Info as InfoIcon,
  Animation as AnimationIcon,
  Contrast as ContrastIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon
} from
  '@mui/icons-material';

// Import context and presets - using only ThemeContext
import { useThemeSettings } from '../contexts/ThemeContext';
import { presets } from '../theme/theme-color-presets';
import ResponsiveTabsAccordion from './ResponsiveTabsAccordion';

// Typography options
const typographyOptions = [
  { name: 'Montserrat', value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { name: 'Roboto', value: 'Roboto, sans-serif', label: 'Roboto' },
  { name: 'Open Sans', value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { name: 'Lato', value: 'Lato, sans-serif', label: 'Lato' },
  { name: 'Poppins', value: 'Poppins, sans-serif', label: 'Poppins' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro, sans-serif', label: 'Source Sans Pro' },
  { name: 'Raleway', value: 'Raleway, sans-serif', label: 'Raleway' },
  { name: 'Nunito', value: 'Nunito, sans-serif', label: 'Nunito' }];


// Language options
const languageOptions = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'avl', name: 'Antwerps-Vlaams', flag: '🇧🇪' },
  { code: 'wvl', name: 'West-Vlaams', flag: '🇧🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];


export default function ThemeSwitcher() {
  const {
    themeMode,
    themeColor,
    typography = 'Montserrat, sans-serif',
    textSize = 'medium',
    contrastMode = false,
    reducedMotion = false,
    language = 'en',
    setThemeMode,
    setThemeColor,
    setTypography,
    setTextSize,
    setContrastMode,
    setReducedMotion,
    changeLanguage, // Using changeLanguage from integrated ThemeContext
    t // Translation function from i18n
  } = useThemeSettings();

  const [activeTab, setActiveTab] = useState(0);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Change theme color
  const handleChangeColor = (colorName) => {
    setThemeColor(colorName);
  };

  // Change typography
  const handleChangeTypography = (fontFamily) => {
    setTypography(fontFamily);
  };

  // Change language - using the integrated changeLanguage function
  const handleChangeLanguage = (languageCode) => {
    changeLanguage(languageCode);
  };

  // Reset all settings to defaults
  const handleResetAll = () => {
    setThemeMode('light');
    setThemeColor('plum');
    setTypography('Montserrat, sans-serif');
    setTextSize('medium');
    setContrastMode(false);
    setReducedMotion(false);
    changeLanguage('en');
  };

  // Define tabs for ResponsiveTabsAccordion
  const themeTabs = [
    {
      label: t ? t('settings.mode') : 'Mode',
      icon: <LightModeIcon />,
      content:
        <Box>
          <Typography variant="h6" gutterBottom>
            {t ? t('settings.themeMode') : 'Theme Mode'}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Card
                sx={{
                  position: 'relative',
                  bgcolor: '#fff',
                  color: '#000',
                  border: themeMode === 'light' ? '2px solid' : '1px solid',
                  borderColor: themeMode === 'light' ? 'primary.main' : 'divider',
                  overflow: 'hidden'
                }}>

                <CardActionArea
                  onClick={() => setThemeMode('light')}
                  sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                  <LightModeIcon fontSize="large" sx={{ mb: 1 }} />
                  <Typography variant="subtitle1">
                    {t ? t('settings.light') : 'Light'}
                  </Typography>

                  {themeMode === 'light' &&
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>

                      <CheckIcon fontSize="small" />
                    </Box>
                  }
                </CardActionArea>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                sx={{
                  position: 'relative',
                  bgcolor: '#121212',
                  color: '#fff',
                  border: themeMode === 'dark' ? '2px solid' : '1px solid',
                  borderColor: themeMode === 'dark' ? 'primary.main' : 'divider'
                }}>

                <CardActionArea
                  onClick={() => setThemeMode('dark')}
                  sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                  <DarkModeIcon fontSize="large" sx={{ mb: 1 }} />
                  <Typography variant="subtitle1">
                    {t ? t('settings.dark') : 'Dark'}
                  </Typography>

                  {themeMode === 'dark' &&
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>

                      <CheckIcon fontSize="small" />
                    </Box>
                  }
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </Box>

    },
    {
      label: t ? t('settings.colors') : 'Colors',
      icon: <ColorIcon />,
      content:
        <Box>
          <Typography variant="h6" gutterBottom>
            {t ? t('settings.themeColors') : 'Theme Colors'}
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            {t ? t('settings.colorDescription') : 'Choose a color scheme for your interface'}
          </Typography>

          {/* Brand Colors Section */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t ? t('settings.brandColors') : 'Brand Colors'}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Plum */}
            <Grid item xs={6}>
              <Card
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  border: themeColor === 'plum' ? '2px solid' : '1px solid',
                  borderColor: themeColor === 'plum' ? '#9a3f69' : 'divider',
                  boxShadow: themeColor === 'plum' ? '0 0 10px rgba(154, 63, 105, 0.3)' : 'none'
                }}>

                <CardActionArea
                  onClick={() => handleChangeColor('plum')}
                  sx={{ p: 0 }}>

                  {/* Color preview with gradient */}
                  <Box
                    sx={{
                      height: 80,
                      background: 'linear-gradient(135deg, #9A3F69 0%, #B15483 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>

                    {themeColor === 'plum' &&
                      <CheckIcon sx={{ color: '#FFFFFF' }} />
                    }
                  </Box>

                  {/* Color name */}
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t ? t("ThemeSwitcher.plum") : "Plum"}

                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t ? t("ThemeSwitcher.9a3f69") : "#9A3F69"}

                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>

            {/* Gold */}
            <Grid item xs={6}>
              <Card
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  border: themeColor === 'gold' ? '2px solid' : '1px solid',
                  borderColor: themeColor === 'gold' ? '#daa03d' : 'divider',
                  boxShadow: themeColor === 'gold' ? '0 0 10px rgba(218, 160, 61, 0.3)' : 'none'
                }}>

                <CardActionArea
                  onClick={() => handleChangeColor('gold')}
                  sx={{ p: 0 }}>

                  {/* Color preview with gradient */}
                  <Box
                    sx={{
                      height: 80,
                      background: 'linear-gradient(135deg, #DAA03D 0%, #E5BA6D 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>

                    {themeColor === 'gold' &&
                      <CheckIcon sx={{ color: '#000000' }} />
                    }
                  </Box>

                  {/* Color name */}
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t ? t("ThemeSwitcher.gold") : "Gold"}

                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t ? t("ThemeSwitcher.daa03d") : "#DAA03D"}

                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>

          {/* Other Colors Section */}
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            {t ? t('settings.additionalColors') : 'Additional Colors'}
          </Typography>

          <Grid container spacing={2}>
            {presets.
              filter((preset) => preset.name !== 'plum' && preset.name !== 'gold').
              map((preset) =>
                <Grid item xs={12} sm={6} md={4} key={preset.name}>
                  <Card
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      border: themeColor === preset.name ? '2px solid' : '1px solid',
                      borderColor: themeColor === preset.name ? preset.primary.main : 'divider',
                      boxShadow: themeColor === preset.name ? `0 0 10px ${alpha(preset.primary.main, 0.3)}` : 'none'
                    }}>

                    <CardActionArea
                      onClick={() => handleChangeColor(preset.name)}
                      sx={{ p: 0 }}>

                      {/* Color preview banner */}
                      <Box
                        sx={{
                          height: 48,
                          bgcolor: preset.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>

                        {themeColor === preset.name &&
                          <CheckIcon sx={{ color: preset.primary.contrastText }} />
                        }
                      </Box>

                      {/* Color name */}
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {preset.name}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              )}
          </Grid>
        </Box>

    },
    {
      label: t ? t('settings.typography') : 'Typography',
      icon: <TypographyIcon />,
      content:
        <Box>
          <Typography variant="h6" gutterBottom>
            {t ? t('settings.typography') : 'Typography'}
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            {t ? t('settings.typographyDescription') : 'Select a font for your interface'}
          </Typography>

          <Grid container spacing={2}>
            {typographyOptions.map((option) =>
              <Grid item xs={12} sm={6} md={4} key={option.name}>
                <Card
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    border: typography === option.value ? '2px solid' : '1px solid',
                    borderColor: typography === option.value ? 'primary.main' : 'divider',
                    height: '100%'
                  }}>

                  <CardActionArea
                    onClick={() => handleChangeTypography(option.value)}
                    sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                    <Typography
                      variant="h5"
                      sx={{
                        mb: 1,
                        fontFamily: option.value,
                        fontWeight: 'bold'
                      }}>{t ? t("ThemeSwitcher.aa") : "Aa"}


                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: option.value,
                        textAlign: 'center'
                      }}>

                      {option.label}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: option.value,
                        mt: 1,
                        display: 'block',
                        color: 'text.secondary',
                        fontSize: '0.7rem'
                      }}>{t ? t("ThemeSwitcher.abcdefghijklm") : "abcdefghijklm"}


                    </Typography>

                    {typography === option.value &&
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>

                        <CheckIcon sx={{ fontSize: 16 }} />
                      </Box>
                    }
                  </CardActionArea>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>

    },
    {
      label: t ? t('settings.accessibility') : 'Accessibility',
      icon: <AccessibilityIcon />,
      content:
        <Box>
          <Typography variant="h6" gutterBottom>
            {t ? t('settings.accessibilitySettings') : 'Accessibility Settings'}
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            {t ? t('settings.accessibilityDescription') : 'Customize your experience to improve accessibility'}
          </Typography>

          {/* Text Size */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TypographyIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1">
                {t ? t('settings.textSize') : 'Text Size'}
              </Typography>
              <Tooltip title={t ? t('settings.textSizeTooltip') : 'Adjust the size of text throughout the application'}>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                row
                value={textSize}
                onChange={(e) => setTextSize(e.target.value)}>

                <FormControlLabel value="small" control={<Radio />} label={t ? t('settings.small') : 'Small'} />
                <FormControlLabel value="medium" control={<Radio />} label={t ? t('settings.medium') : 'Medium'} />
                <FormControlLabel value="large" control={<Radio />} label={t ? t('settings.large') : 'Large'} />
                <FormControlLabel value="xlarge" control={<Radio />} label={t ? t('settings.extraLarge') : 'Extra Large'} />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* High Contrast */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ContrastIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1">
                {t ? t('settings.highContrast') : 'High Contrast'}
              </Typography>
              <Tooltip title={t ? t('settings.highContrastTooltip') : 'Increase contrast for better readability'}>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={contrastMode}
                  onChange={(e) => setContrastMode(e.target.checked)} />

              }
              label={t ? t('settings.enableHighContrast') : 'Enable high contrast mode'} />

          </Box>

          {/* Reduced Motion */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AnimationIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1">
                {t ? t('settings.motion') : 'Motion'}
              </Typography>
              <Tooltip title={t ? t('settings.motionTooltip') : 'Reduce animations and motion effects'}>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={reducedMotion}
                  onChange={(e) => setReducedMotion(e.target.checked)} />

              }
              label={t ? t('settings.reduceMotion') : 'Reduce animations and motion'} />

          </Box>
        </Box>

    },
    {
      label: t ? t('settings.language') : 'Language',
      icon: <LanguageIcon />,
      content:
        <Box>
          <Typography variant="h6" gutterBottom>
            {t ? t('settings.languageRegion') : 'Language & Region'}
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            {t ? t('settings.languageDescription') : 'Select your preferred language for the interface'}
          </Typography>

          <Grid container spacing={2}>
            {languageOptions.map((option) =>
              <Grid item xs={12} sm={6} md={4} key={option.code}>
                <Card
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    border: language === option.code ? '2px solid' : '1px solid',
                    borderColor: language === option.code ? 'primary.main' : 'divider',
                    height: '100%',
                    boxShadow: language === option.code ? '0 0 10px rgba(0, 0, 0, 0.1)' : 'none'
                  }}>

                  <CardActionArea
                    onClick={() => handleChangeLanguage(option.code)}
                    sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                    <Typography
                      variant="h3"
                      sx={{
                        mb: 1,
                        fontSize: '2rem'
                      }}>

                      {option.flag}
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        textAlign: 'center',
                        fontWeight: language === option.code ? 600 : 400
                      }}>

                      {option.name}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem'
                      }}>

                      {option.code}
                    </Typography>

                    {language === option.code &&
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>

                        <CheckIcon sx={{ fontSize: 16 }} />
                      </Box>
                    }
                  </CardActionArea>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Additional Language Info */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TranslateIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle2">
                {t ? t('settings.languageSettings') : 'Language Settings'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t ? t('settings.languageChangeNote') : 'Changing the language will update all interface text. Some content may remain in the original language until the page is refreshed.'}
            </Typography>
          </Box>
        </Box>

    }];


  return (
    <Paper sx={{ overflow: 'hidden', mb: 3, maxWidth: '100%' }}>
      <ResponsiveTabsAccordion
        tabs={themeTabs}
        value={activeTab}
        onChange={handleTabChange}
        defaultAllClosed={true} />


      <Divider />

      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t ? t('settings.autoSave') : 'Preferences are saved automatically'}
        </Typography>

        <Button
          size="small"
          onClick={handleResetAll}>

          {t ? t('settings.resetAll') : 'Reset All to Default'}
        </Button>
      </Box>
    </Paper>);

}